/**
 * The walk's stick for a thumb, as the app's joystick (`UISimpleJoystick`): a disc at the stage's bottom-left — in the
 * middle on a phone held upright — over the walk / orbit switch, where the orbit's walls button stands. The knob follows
 * the thumb inside the disc, and how far and which way it is pushed walks the walker through `Viewer.setWalkStick`:
 * forward up the screen, sideways across it, at 1 m/s at the rim and speeding up to 2 m/s, the twin of WASD with the same
 * walls and doors. Another finger on the picture still looks around, so a thumb walks while a finger turns.
 * The first walk on a touch screen explains it once.
 */
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { t } from '../i18n/I18n';

/** The disc and the knob, CSS pixels; the knob's centre travels to the disc's rim less a margin. */
export const STICK_SIZE = 132;
export const KNOB_SIZE = 52;
const TRAVEL = STICK_SIZE / 2 - KNOB_SIZE / 2 - 2;
/** A push shorter than this is a resting thumb, not a step. */
const DEAD_ZONE = .12;
const HINT_KEY = 'hp.hint.walkTouch';
const HINT_DELAY_MS = 1500;

/** The knob's offset in the disc → the walk's push: forward up the screen, sideways to the right, linear as the app's. */
export function stickPush(dx: number, dy: number, travel = TRAVEL): { fwd: number; strafe: number; knob: { x: number; y: number } } {
  const length = Math.hypot(dx, dy);
  const reach = Math.min(length, travel), knob = length ? { x: dx / length * reach, y: dy / length * reach } : { x: 0, y: 0 };
  const share = reach / travel;
  if (share <= DEAD_ZONE) return { fwd: 0, strafe: 0, knob };
  const pace = (share - DEAD_ZONE) / (1 - DEAD_ZONE);
  return { fwd: -dy / length * pace, strafe: dx / length * pace, knob };
}

/** Whether this screen can be touched — also a laptop with a touch screen, which may walk with the keys too. */
export function useTouchScreen(): boolean {
  const [touch, setTouch] = useState(() => typeof matchMedia === 'function' && matchMedia('(any-pointer: coarse)').matches);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const query = matchMedia('(any-pointer: coarse)'), change = () => setTouch(query.matches);
    change();
    query.addEventListener('change', change);
    return () => query.removeEventListener('change', change);
  }, []);
  return touch;
}

const hintSeen = () => { try { return localStorage.getItem(HINT_KEY) === '1'; } catch { return true; } };
const markHintSeen = () => { try { localStorage.setItem(HINT_KEY, '1'); } catch { /* shown again next time */ } };

export function WalkStick({ onPush }: { onPush: (fwd: number, strafe: number) => void }) {
  const [knob, setKnob] = useState<{ x: number; y: number } | null>(null);
  const [hint, setHint] = useState(false);
  const held = useRef<number | null>(null);
  const push = useRef(onPush); push.current = onPush;
  // Leaving the walk or the stage lets go of the stick.
  useEffect(() => () => { if (held.current !== null) push.current(0, 0); }, []);
  // The first walk on a touch screen says once what the fingers do, a moment after the walk appears.
  useEffect(() => {
    if (hintSeen()) return;
    const timer = setTimeout(() => setHint(true), HINT_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);
  const closeHint = () => { setHint(false); markHintSeen(); };
  const follow = (event: ReactPointerEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const next = stickPush(event.clientX - (box.left + box.width / 2), event.clientY - (box.top + box.height / 2));
    setKnob(next.knob);
    push.current(next.fwd, next.strafe);
  };
  const release = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (held.current !== event.pointerId) return;
    held.current = null;
    setKnob(null);
    push.current(0, 0);
  };
  return <div className="walk-stick-place">
    {hint && <div className="walk-hint" role="status">
      <p>{t('#EditorWalkStickHint')}</p>
      <button type="button" className="text-button" onClick={closeHint}>{t('#GotIt')}</button>
    </div>}
    <div className="walk-stick" role="group" aria-label={t('#EditorWalkStickLabel')} data-held={knob !== null}
      onPointerDown={event => {
        if (held.current !== null || (event.pointerType === 'mouse' && event.button !== 0)) return;
        event.preventDefault(); event.stopPropagation();
        if (hint) closeHint();
        held.current = event.pointerId;
        event.currentTarget.setPointerCapture(event.pointerId);
        follow(event);
      }}
      onPointerMove={event => { if (held.current === event.pointerId) { event.preventDefault(); follow(event); } }}
      onPointerUp={release} onPointerCancel={release} onLostPointerCapture={release}
      onContextMenu={event => event.preventDefault()}>
      <svg className="walk-stick-marks" viewBox="-66 -66 132 132" aria-hidden="true">
        <path d="M-5 -50 0 -55 5 -50M-5 50 0 55 5 50M-50 -5 -55 0 -50 5M50 -5 55 0 50 5" />
      </svg>
      <span className="walk-stick-knob" style={{ transform: `translate(${knob?.x ?? 0}px, ${knob?.y ?? 0}px)` }} />
    </div>
  </div>;
}
